import { useUser } from '@clerk/nextjs';

import { logger } from '../utils/api/logger';
import type { IntercomContact } from '../utils/intercom/intercom-server';

interface IntercomApiData {
  secureHash?: string;
  intercomContact?: IntercomContact | null;
}

interface IntercomBootConfig {
  app_id: string | undefined;
  email: string;
  hide_default_launcher: boolean;
  user_hash?: string;
  user_id?: string;
}

export const useIntercom = () => {
  const { user } = useUser();

  const initializeIntercom = async () => {
    if (!(global as any).Intercom || !user) {
      return;
    }

    const userEmail = user.primaryEmailAddress?.emailAddress;
    if (!user.id || !userEmail) {
      logger.warn(
        { user_id: user.id, userEmail },
        'User ID or primary email missing, cannot initialize Intercom with identity.',
      );
      return;
    }

    try {
      const email = encodeURIComponent(userEmail);
      const response = await fetch(
        `/api/user/provider?userId=${user.id}&email=${email}`,
      );

      if (!response.ok) {
        throw new Error(`API request failed with status ${response.status}`);
      }

      const data: IntercomApiData = await response.json();

      const intercomConfig: IntercomBootConfig = {
        app_id: process.env.NEXT_PUBLIC_INTERCOM_APP_ID,
        email: userEmail,
        hide_default_launcher: false,
      };

      if (data.secureHash) {
        intercomConfig.user_hash = data.secureHash;
      } else {
        logger.warn(
          { user_id: user.id, email: userEmail },
          'No secure hash received from API for Intercom boot. Identity verification might be disabled.',
        );
      }

      if (data.intercomContact?.external_id) {
        intercomConfig.user_id = data.intercomContact.external_id;
      } else {
        logger.info(
          { user_id: user.id, email: userEmail },
          'No Intercom external_id found in API response. Intercom will associate by email.',
        );
      }

      logger.info(
        {
          user_id: user.id,
          email: userEmail,
          hasHash: !!data.secureHash,
          hasUserId: !!intercomConfig.user_id,
        },
        'Booting Intercom...',
      );
      (global as any).Intercom?.('boot', intercomConfig);
    } catch (error) {
      logger.error(
        {
          error: error instanceof Error ? error.message : String(error),
          user_id: user.id,
          email: userEmail,
        },
        'Failed to fetch Intercom data or boot Intercom',
      );
    }
  };

  const shutdownIntercom = () => {
    logger.info({}, 'Shutting down Intercom...');
    (global as any).Intercom?.('shutdown');
  };

  return { initializeIntercom, shutdownIntercom };
};
